// Display formatting for the admin console. Dates show in the admin's own
// time zone; every number, date and id renders in the mono font at the call site.

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const p2 = (n: number) => String(n).padStart(2, "0");

/** Analytics Engine returns "2026-10-12 21:31:00" (UTC); D1 returns ISO. */
export function parseWhen(value: string): Date {
  return new Date(/^\d{4}-\d{2}-\d{2} \d/.test(value) ? `${value.replace(" ", "T")}Z` : value);
}

/** A UTC calendar day ("2026-10-12") — formatted without shifting zones. */
function dayDate(day: string): Date {
  return new Date(`${day}T12:00:00Z`);
}

export const n0 = (n: number) => n.toLocaleString("en-GB");

/** 12 Oct */
export const fD = (d: Date) => `${d.getDate()} ${MON[d.getMonth()]}`;
/** 12 Oct 2026 */
export const fDY = (d: Date) => `${fD(d)} ${d.getFullYear()}`;
/** 21:31 */
export const fT = (d: Date) => `${p2(d.getHours())}:${p2(d.getMinutes())}`;
/** 12 Oct, 21:31 */
export const fDT = (d: Date) => `${fD(d)}, ${fT(d)}`;

/** Mon 12 Oct (a UTC day) */
export function fWDay(day: string): string {
  const d = dayDate(day);
  return `${WD[d.getUTCDay()]} ${d.getUTCDate()} ${MON[d.getUTCMonth()]}`;
}
/** 12 Oct (a UTC day) */
export function fDay(day: string): string {
  const d = dayDate(day);
  return `${d.getUTCDate()} ${MON[d.getUTCMonth()]}`;
}

/** just now · 12 min ago · 5 h ago · 3 d ago · 12 Oct */
export function fRel(d: Date, now: Date = new Date()): string {
  const m = (now.getTime() - d.getTime()) / 60_000;
  if (m < 1) return "just now";
  if (m < 60) return `${Math.round(m)} min ago`;
  const h = m / 60;
  if (h < 24) return `${Math.round(h)} h ago`;
  const days = Math.round(h / 24);
  return days <= 14 ? `${days} d ago` : fD(d);
}

/** "Safari · iPhone" from a user agent; null-safe. */
export function deviceLabel(ua: string | null): string {
  if (!ua) return "Unknown device";
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Firefox\//.test(ua)
      ? "Firefox"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Safari\//.test(ua)
          ? "Safari"
          : /curl\//.test(ua)
            ? "curl"
            : "Browser";
  const os = /iPhone/.test(ua)
    ? "iPhone"
    : /iPad/.test(ua)
      ? "iPad"
      : /Android/.test(ua)
        ? "Android"
        : /Mac OS X|Macintosh/.test(ua)
          ? "macOS"
          : /Windows/.test(ua)
            ? "Windows"
            : /Linux/.test(ua)
              ? "Linux"
              : null;
  return os ? `${browser} · ${os}` : browser;
}

export const FORMAT_LABEL: Record<string, string> = {
  poster_pdf: "Poster PDF",
  weekly_pdf: "Weekly cards PDF",
  ics: "ICS",
  json: "JSON",
  fit: "FIT",
};
export const FORMAT_TAG: Record<string, string> = { poster_pdf: "POSTER", weekly_pdf: "CARDS", ics: "ICS", json: "JSON", fit: "FIT" };

export const METHOD_LABEL: Record<string, string> = { password: "Password", google: "Google", apple: "Apple", microsoft: "Microsoft" };
export const methodLabel = (m: string) => METHOD_LABEL[m] ?? m.charAt(0).toUpperCase() + m.slice(1);

export const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;

/** A duration as "1 min" · "9 min" · "3 h" · "2 d". */
export function fSpan(ms: number): string {
  if (ms < 3_600_000) return `${Math.max(1, Math.round(ms / 60_000))} min`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)} h`;
  return `${Math.round(ms / 86_400_000)} d`;
}

/** Tracked export formats, in the order the console lists them. */
export const FORMATS = ["poster_pdf", "weekly_pdf", "json", "fit"] as const;
